import { type Ref, computed, nextTick, ref } from "vue";

import cloneDeep from "lodash/cloneDeep";

import { AllowedKey, EditorNodeName } from "@/enums/customAttributes";
import { BracketOperator } from "@/enums/customAttributesV2";
import {
  BRACKET_COLORS,
  DELIMITERS,
} from "@/helpers/constants/attributeBuilder";
import { findActiveBracketPair } from "@/helpers/parser";
import {
  getSerializedNodeLength,
  nodePositionToFormulaOffset,
} from "@/helpers/parser/cursorMapping";
import { type ParsedFormulaMetadata } from "@/helpers/parser/visitor";
import { type useFormulaEditor } from "@/hooks/attributeBuilder";
import { useEditorParserIntegration } from "@/hooks/useEditorParserIntegration";

import type { BracketContext, EditorNode } from "@/models/TextEditor";
import type { FormulaOption, FormulaParameter } from "@/models/options";
import type { IAttribute } from "@/models/orchestration";

export enum SelectionDirection {
  ltr = "ltr",
  rtl = "rtl",
}

export interface EditorRange {
  start: number;
  startOffset: number;
  endOffset: number;
  end: number;
  selectionDirection: SelectionDirection;
}

interface ActiveBracketPairRange {
  startIndex?: number;
  endIndex?: number;
}

interface LocalFunctionDefinition {
  definition?: FormulaOption;
  arguments: EditorNode[][];
  optionsSet: Record<string, string | undefined>;
}

interface PositionResult {
  newIndex: number;
  newOffset: number;
}

// 1. CORE EDITOR STATE AND SELECTION MANAGEMENT
// Managing editor state, selection, and DOM interactions

interface ComponentRef {
  $el?: HTMLElement | { value: HTMLElement | null };
  getElement?: () => HTMLElement | null;
}

export const useEditorCore = (
  editorContent: Ref<EditorNode[]>,
  componentRefs: Ref<(HTMLElement | ComponentRef)[]>
) => {
  const selectionRange = ref<EditorRange | null>(null);
  const isUpdatingRange = ref(false);

  const isAtomicNode = (node?: EditorNode): boolean => !!node?.atomic;
  const hasValidContent = (node?: EditorNode): boolean =>
    !!node?.content?.length;
  const clampPosition = (position: number, max: number): number =>
    Math.max(0, Math.min(position, max));
  const validateIndex = (index: number): boolean =>
    index >= 0 && index < editorContent.value.length;

  const getElementFromRef = (
    ref: HTMLElement | ComponentRef
  ): HTMLElement | null => {
    const componentRef = ref as ComponentRef;

    if (typeof componentRef.getElement === "function") {
      return componentRef.getElement();
    }

    if (!componentRef.$el) {
      return ref as HTMLElement;
    }

    const element = componentRef.$el;
    const isRefObject =
      element && typeof element === "object" && "value" in element;

    return isRefObject ? element.value : (element as HTMLElement);
  };

  const getEditorElement = (): HTMLElement | null => {
    const firstRef = componentRefs.value[0];
    if (!firstRef) {
      return null;
    }
    const refElement = getElementFromRef(firstRef);
    return refElement?.parentElement ?? null;
  };

  const currentNodeIndex = computed(() => selectionRange.value?.start || 0);
  const currentNode = computed(
    () => editorContent.value[currentNodeIndex.value]
  );
  const nextNode = computed(
    () => editorContent.value[currentNodeIndex.value + 1]
  );
  const previousNode = computed(
    () => editorContent.value[currentNodeIndex.value - 1]
  );

  const getNodeElement = (nodeIndex: number): HTMLElement | null => {
    if (!validateIndex(nodeIndex)) {
      return null;
    }

    const editorEl = getEditorElement();
    if (!editorEl || nodeIndex >= editorEl.children.length) {
      return null;
    }

    const element = editorEl.children[nodeIndex] as HTMLElement;
    if (!element) {
      return null;
    }

    return getContentElement(element);
  };

  const createDOMRange = (
    startNode: Node,
    startOffset: number,
    collapsed = true
  ): Range => {
    const range = document.createRange();
    range.setStart(startNode, startOffset);
    if (collapsed) {
      range.collapse(true);
    }
    return range;
  };

  const applyDOMSelection = (range: Range): boolean => {
    const windowSelection = window.getSelection();
    if (!windowSelection) {
      return false;
    }

    try {
      windowSelection.removeAllRanges();
      windowSelection.addRange(range);
      return true;
    } catch {
      return false;
    }
  };

  const setDOMSelection = async (
    nodeIndex: number,
    cursorPosition: number
  ): Promise<boolean> => {
    const nodeElement = getNodeElement(nodeIndex);
    if (!nodeElement?.lastChild) {
      return false;
    }

    const textNode = nodeElement.lastChild;
    const textLength = textNode.textContent?.length ?? 0;
    const safeCursorPosition = Math.min(cursorPosition, textLength);

    try {
      const range = createDOMRange(textNode, safeCursorPosition);
      return applyDOMSelection(range);
    } catch {
      return false;
    }
  };

  const createSelectionRange = (
    start: number,
    startOffset: number,
    end: number = start,
    endOffset: number = startOffset,
    direction: SelectionDirection = SelectionDirection.ltr
  ): EditorRange => ({
    start,
    startOffset,
    end,
    endOffset,
    selectionDirection: direction,
  });

  const createFallbackSelection = (nodeIndex: number): EditorRange => {
    const fallbackIndex = clampPosition(
      nodeIndex,
      editorContent.value.length - 1
    );
    const fallbackNode = editorContent.value[fallbackIndex];
    const fallbackOffset = fallbackNode?.content?.length ?? 0;
    return createSelectionRange(fallbackIndex, fallbackOffset);
  };

  const isValidSelection = (): boolean => {
    if (!selectionRange.value || !editorContent.value.length) {
      return false;
    }

    const { start, end, startOffset, endOffset } = selectionRange.value;

    if (!validateIndex(start) || !validateIndex(end)) {
      return false;
    }

    const startNode = editorContent.value[start];
    const endNode = editorContent.value[end];

    if (!startNode || !endNode) {
      return false;
    }

    return !(
      startOffset < 0 ||
      startOffset > (startNode.content?.length ?? 0) ||
      endOffset < 0 ||
      endOffset > (endNode.content?.length ?? 0)
    );
  };

  const updateAtomicNodeSelections = (): void => {
    if (!selectionRange.value) {
      return;
    }

    const { start, end } = selectionRange.value;

    // We need to clear all atomic nodes first, then set the selected ones
    // to ensure previously selected nodes outside the range are deselected
    editorContent.value.forEach((node, index) => {
      if (!node.atomic) {
        return;
      }

      const shouldBeSelected = start <= index && index <= end;
      if (node.isSelected !== shouldBeSelected) {
        node.isSelected = shouldBeSelected;
      }
    });
  };

  const updateCursorPosition = async (
    nodeIndex: number,
    cursorPosition: number
  ): Promise<void> => {
    if (!validateIndex(nodeIndex)) {
      const fallbackRange = createFallbackSelection(nodeIndex);
      selectionRange.value = fallbackRange;
      return;
    }

    const clampedPosition = clampPosition(
      cursorPosition,
      editorContent.value[nodeIndex]?.content?.length ?? 0
    );
    const nodeElement = getNodeElement(nodeIndex);

    selectionRange.value = createSelectionRange(nodeIndex, clampedPosition);
    updateAtomicNodeSelections();

    if (nodeElement?.lastChild) {
      await setDOMSelection(nodeIndex, clampedPosition);
    }
  };

  const contentElementsCache = new Map<HTMLElement, HTMLElement>();

  const getContentElement = (
    componentElement: HTMLElement
  ): HTMLElement | null => {
    if (contentElementsCache.has(componentElement)) {
      const cachedElement = contentElementsCache.get(componentElement)!;
      // Verify cached element is still connected to DOM
      if (cachedElement.isConnected) {
        return cachedElement;
      }
      // Remove stale cache entry
      contentElementsCache.delete(componentElement);
    }

    const contentElement = componentElement.querySelector(
      "[data-node-content]"
    ) as HTMLElement;
    if (contentElement) {
      contentElementsCache.set(componentElement, contentElement);
    }
    return contentElement;
  };

  const handleSelectionChange = (): void => {
    if (isUpdatingRange.value) {
      return;
    }

    const selection = window.getSelection();
    if (!selection?.rangeCount) {
      return;
    }

    const range = selection.getRangeAt(0);
    const { startContainer, endContainer, startOffset, endOffset } = range;

    const editorEl = getEditorElement();
    if (!editorEl) {
      return;
    }

    if (!editorEl.contains(startContainer) && editorEl !== startContainer) {
      return;
    }

    let startNodeIndex = -1;
    let endNodeIndex = -1;
    let startTextOffset = startOffset;
    let endTextOffset = endOffset;

    const children = editorEl.children;

    for (let i = 0; i < children.length; i++) {
      const componentElement = children[i] as HTMLElement;
      if (!componentElement) {
        continue;
      }

      const contentElement = getContentElement(componentElement);
      if (!contentElement) {
        continue;
      }

      if (
        startNodeIndex === -1 &&
        (contentElement.contains(startContainer) ||
          contentElement === startContainer)
      ) {
        startNodeIndex = i;
        startTextOffset =
          startContainer.nodeType === Node.TEXT_NODE
            ? startOffset
            : startOffset > 0
              ? (editorContent.value[i]?.content?.length ?? 0)
              : 0;
      }

      if (
        endNodeIndex === -1 &&
        (contentElement.contains(endContainer) ||
          contentElement === endContainer)
      ) {
        endNodeIndex = i;
        endTextOffset =
          endContainer.nodeType === Node.TEXT_NODE
            ? endOffset
            : endOffset > 0
              ? (editorContent.value[i]?.content?.length ?? 0)
              : 0;
      }

      if (startNodeIndex !== -1 && endNodeIndex !== -1) {
        break;
      }
    }

    if (startNodeIndex >= 0 && endNodeIndex >= 0) {
      const startMaxOffset =
        editorContent.value[startNodeIndex]?.content?.length ?? 0;
      const endMaxOffset =
        editorContent.value[endNodeIndex]?.content?.length ?? 0;

      const clampedStartOffset = Math.min(
        Math.max(0, startTextOffset),
        startMaxOffset
      );
      const clampedEndOffset = Math.min(
        Math.max(0, endTextOffset),
        endMaxOffset
      );

      selectionRange.value = {
        start: startNodeIndex,
        startOffset: clampedStartOffset,
        end: endNodeIndex,
        endOffset: clampedEndOffset,
        selectionDirection: SelectionDirection.ltr,
      };
      updateAtomicNodeSelections();
    }
  };

  return {
    selectionRange,
    isUpdatingRange,
    currentNodeIndex,
    currentNode,
    nextNode,
    previousNode,
    isAtomicNode,
    hasValidContent,
    clampPosition,
    validateIndex,
    getNodeElement,
    setDOMSelection,
    createSelectionRange,
    createFallbackSelection,
    isValidSelection,
    updateAtomicNodeSelections,
    updateCursorPosition,
    handleSelectionChange,
  };
};

// =============================================================================
// 2. NAVIGATION AND INPUT HANDLING
// Handle user input (keys, navigation, character input)
// =============================================================================

export const useEditorInput = (
  editorContent: Ref<EditorNode[]>,
  core: ReturnType<typeof useEditorCore>,
  formulaEditor: ReturnType<typeof useFormulaEditor>,
  isProcessingKeyInput: Ref<boolean>,
  createEditorNode: (content: string, type: EditorNodeName) => EditorNode,
  updateEditorContent: (
    start: number,
    end: number,
    newNodes?: EditorNode[]
  ) => Promise<void>,
  calculateNewPosition: (nodes: EditorNode[]) => PositionResult,
  validateEditorState: () => boolean,
  contentComposable: {
    shouldDeleteAsRange: () => boolean;
    deleteSingleCharacter: (nodeIndex: number) => Promise<void>;
    deleteSelectedRange: () => Promise<void>;
  }
) => {
  const { tokenize } = formulaEditor;

  const shouldInsertUncategorizedNode = (
    currentNode: EditorNode,
    previousNode?: EditorNode,
    currentNodeIndex?: number
  ): boolean => {
    return currentNode.atomic && !previousNode && currentNodeIndex === 0;
  };

  const handleAtomicNodeNavigation = async (
    key: AllowedKey.arrowLeft | AllowedKey.arrowRight,
    currentNodeIndex: number,
    currentNode: EditorNode,
    previousNode?: EditorNode,
    nextNode?: EditorNode
  ): Promise<{ newNodeIndex: number; newPosition: number } | null> => {
    if (key === AllowedKey.arrowLeft) {
      if (
        shouldInsertUncategorizedNode(
          currentNode,
          previousNode,
          currentNodeIndex
        )
      ) {
        editorContent.value.splice(
          currentNodeIndex,
          0,
          createEditorNode("", EditorNodeName.uncategorized)
        );
        await core.updateCursorPosition(currentNodeIndex, 0);
        return null;
      }

      if (currentNodeIndex > 0) {
        const newNodeIndex = currentNodeIndex - 1;
        const newPosition =
          editorContent.value[newNodeIndex]?.content?.length ?? 0;
        return { newNodeIndex, newPosition };
      }
    } else {
      if (currentNodeIndex < editorContent.value.length - 1) {
        const newPosition = nextNode?.atomic
          ? (nextNode.content?.length ?? 0)
          : 0;
        return { newNodeIndex: currentNodeIndex + 1, newPosition };
      }
    }

    return null;
  };

  const handleNonAtomicNodeNavigation = (
    key: AllowedKey.arrowLeft | AllowedKey.arrowRight,
    currentNodeIndex: number,
    currentOffset: number,
    currentNodeElement: HTMLElement | null,
    nextNode?: EditorNode
  ): { newNodeIndex: number; newPosition: number } => {
    if (key === AllowedKey.arrowLeft) {
      const newPosition = Math.max(0, currentOffset - 1);

      if (newPosition === 0 && currentNodeIndex > 0) {
        const previousNodeElement = core.getNodeElement(currentNodeIndex - 1);
        if (previousNodeElement?.textContent) {
          return {
            newNodeIndex: currentNodeIndex - 1,
            newPosition: previousNodeElement.textContent.length,
          };
        }
      }

      return { newNodeIndex: currentNodeIndex, newPosition };
    } else {
      const maxPosition = currentNodeElement?.textContent?.length ?? 0;
      const newPosition = Math.min(maxPosition, currentOffset + 1);

      if (
        newPosition === maxPosition &&
        currentNodeIndex < editorContent.value.length - 1
      ) {
        const nextPosition = nextNode?.atomic
          ? (nextNode.content?.length ?? 0)
          : 0;
        return {
          newNodeIndex: currentNodeIndex + 1,
          newPosition: nextPosition,
        };
      }

      return { newNodeIndex: currentNodeIndex, newPosition };
    }
  };

  const processArrowKeyNavigation = async (
    key: AllowedKey.arrowLeft | AllowedKey.arrowRight
  ): Promise<void> => {
    const currentNodeIndex = core.currentNodeIndex.value;
    const currentNodeElement = core.getNodeElement(currentNodeIndex);
    const currentNode = core.currentNode.value;

    if (!currentNodeElement || !currentNode) {
      return;
    }

    const hasContent =
      (currentNodeElement.lastChild && currentNodeElement.textContent) ||
      currentNode.type === EditorNodeName.uncategorized;

    if (!hasContent) {
      return;
    }

    const previousNode = core.previousNode.value;
    const nextNode = core.nextNode.value;
    const currentOffset = core.selectionRange.value?.startOffset || 0;

    let navigationResult: { newNodeIndex: number; newPosition: number } | null =
      null;

    if (currentNode.atomic) {
      navigationResult = await handleAtomicNodeNavigation(
        key,
        currentNodeIndex,
        currentNode,
        previousNode,
        nextNode
      );
    } else {
      navigationResult = handleNonAtomicNodeNavigation(
        key,
        currentNodeIndex,
        currentOffset,
        currentNodeElement,
        nextNode
      );
    }

    if (navigationResult) {
      await core.updateCursorPosition(
        navigationResult.newNodeIndex,
        navigationResult.newPosition
      );
    }
  };

  const shouldHandleAsRangeSelection = (): boolean => {
    if (!core.selectionRange.value) {
      return false;
    }

    const { start, end, startOffset, endOffset } = core.selectionRange.value;
    return endOffset !== startOffset || end !== start;
  };

  const shouldRedirectToNextNode = (
    currentNode: EditorNode,
    nextNode?: EditorNode
  ): boolean => {
    return currentNode.atomic && !!nextNode && !nextNode.atomic;
  };

  const shouldIgnoreSpaceInput = (): boolean => {
    return false;
  };

  const calculateTextWithMerging = (
    key: string,
    currentNode: EditorNode,
    previousNode?: EditorNode,
    nextNode?: EditorNode
  ): { text: string; startIndexOffset: number; endIndexOffset: number } => {
    const currentOffset = core.selectionRange.value?.startOffset || 0;
    const nodeContent = currentNode.content ?? "";
    const firstPart = nodeContent.slice(0, currentOffset);
    const lastPart = nodeContent.slice(currentOffset);
    let text = `${firstPart}${key}${lastPart}`;

    let startIndexOffset = 0;
    let endIndexOffset = 0;

    if (
      !currentNode.atomic &&
      !previousNode?.atomic &&
      previousNode?.content?.length
    ) {
      text = previousNode.content + text;
      startIndexOffset = 1;
    }

    if (!nextNode?.atomic && nextNode?.content?.length) {
      text = text + nextNode.content;
      endIndexOffset = 1;
    }

    if (currentNode.atomic) {
      const textAddition = nextNode?.content || "";
      text = key + textAddition;

      if (nextNode?.atomic) {
        text = key;
      }
    }

    return { text, startIndexOffset, endIndexOffset };
  };

  const generateNewNodes = (
    text: string,
    currentNode: EditorNode,
    currentNodeIndex: number,
    startIndexOffset: number
  ): EditorNode[] => {
    const previousNodeForContext =
      editorContent.value[currentNodeIndex - 1 - startIndexOffset];

    return currentNode.atomic
      ? [currentNode, ...tokenize(text, previousNodeForContext)]
      : tokenize(text, previousNodeForContext);
  };

  const handleRangeSelection = async (key: string): Promise<void> => {
    if (!core.selectionRange.value) {
      return;
    }

    const startOfSelection = core.selectionRange.value.start;

    // Delete the selected range first (simulate backspace)
    if (contentComposable.shouldDeleteAsRange()) {
      await contentComposable.deleteSelectedRange();
    } else {
      await contentComposable.deleteSingleCharacter(
        core.currentNodeIndex.value
      );
    }

    await nextTick();

    const isFirstNodeAtomic = !!editorContent.value[0]?.atomic;
    if (isFirstNodeAtomic && startOfSelection === 0) {
      editorContent.value.unshift(
        createEditorNode("", EditorNodeName.uncategorized)
      );
      await processArrowKeyNavigation(AllowedKey.arrowLeft);
    }

    await processCharacterInput(key);
  };

  const processCharacterInput = async (key: string): Promise<void> => {
    if (isProcessingKeyInput.value) {
      return;
    }
    isProcessingKeyInput.value = true;

    try {
      const currentNodeIndex = core.currentNodeIndex.value;
      const currentNode = core.currentNode.value;
      const previousNode = core.previousNode.value;
      const nextNode = core.nextNode.value;

      if (shouldHandleAsRangeSelection()) {
        await handleRangeSelection(key);
        return;
      }

      if (!currentNode) {
        return;
      }

      if (shouldRedirectToNextNode(currentNode, nextNode)) {
        await processArrowKeyNavigation(AllowedKey.arrowRight);
        isProcessingKeyInput.value = false;
        await processCharacterInput(key);
        return;
      }

      const currentNodeElement = core.getNodeElement(currentNodeIndex);
      if (!currentNodeElement) {
        return;
      }

      const { text, startIndexOffset, endIndexOffset } =
        calculateTextWithMerging(key, currentNode, previousNode, nextNode);

      const newNodes = generateNewNodes(
        text,
        currentNode,
        currentNodeIndex,
        startIndexOffset
      );

      const { newIndex, newOffset } = calculateNewPosition(newNodes);

      await updateEditorContent(
        currentNodeIndex - startIndexOffset,
        currentNodeIndex + endIndexOffset + 1,
        newNodes
      );

      const windowSelection = window.getSelection();
      if (!windowSelection || windowSelection.toString().length !== 0) {
        return;
      }

      await nextTick();

      if (validateEditorState() || newIndex < editorContent.value.length) {
        await core.updateCursorPosition(newIndex, newOffset);
      }
    } finally {
      isProcessingKeyInput.value = false;
    }
  };

  return {
    processArrowKeyNavigation,
    processCharacterInput,
    shouldHandleAsRangeSelection,
    shouldRedirectToNextNode,
    shouldIgnoreSpaceInput,
  };
};

// =============================================================================
// 3. CONTENT MANIPULATION AND DELETION
// Handle content updates, deletions, and bracket operations
// =============================================================================

export const useEditorContent = (
  editorContent: Ref<EditorNode[]>,
  core: ReturnType<typeof useEditorCore>,
  formulaEditor: ReturnType<typeof useFormulaEditor>,
  createEditorNode: (content: string, type: EditorNodeName) => EditorNode,
  updateBracketColors: (content: EditorNode[]) => void,
  emit: (event: "update:model-value", value: EditorNode[]) => void
) => {
  const { tokenize } = formulaEditor;

  const createNewContent = (
    start: number,
    end: number,
    newNodes: EditorNode[] = []
  ): EditorNode[] => {
    const newContent = [
      ...editorContent.value.slice(0, start),
      ...newNodes,
      ...editorContent.value.slice(end),
    ];

    if (newContent.length === 0) {
      newContent.push(createEditorNode("", EditorNodeName.uncategorized));
    }

    return newContent;
  };

  const validateSelectionBounds = (
    currentSelection: EditorRange,
    newContent: EditorNode[]
  ): EditorRange => {
    const maxIndex = Math.max(0, newContent.length - 1);
    const validStart = core.clampPosition(currentSelection.start, maxIndex);
    const validEnd = core.clampPosition(currentSelection.end, maxIndex);

    return {
      ...currentSelection,
      start: validStart,
      end: validEnd,
    };
  };

  const validateSelectionOffsets = (
    selection: EditorRange,
    newContent: EditorNode[]
  ): EditorRange => {
    const startNode = newContent[selection.start];
    const endNode = newContent[selection.end];
    const maxStartOffset = startNode?.content?.length ?? 0;
    const maxEndOffset = endNode?.content?.length ?? 0;

    const validStartOffset = core.clampPosition(
      selection.startOffset,
      maxStartOffset
    );
    const validEndOffset = core.clampPosition(
      selection.endOffset,
      maxEndOffset
    );

    return {
      ...selection,
      startOffset: validStartOffset,
      endOffset: validEndOffset,
    };
  };

  const updateSelectionIfNeeded = (newContent: EditorNode[]): void => {
    if (!core.selectionRange.value) {
      return;
    }

    const currentSelection = core.selectionRange.value;
    const boundsValidated = validateSelectionBounds(
      currentSelection,
      newContent
    );
    const fullyValidated = validateSelectionOffsets(
      boundsValidated,
      newContent
    );

    const shouldUpdate =
      fullyValidated.start !== currentSelection.start ||
      fullyValidated.end !== currentSelection.end ||
      fullyValidated.startOffset !== currentSelection.startOffset ||
      fullyValidated.endOffset !== currentSelection.endOffset;

    if (shouldUpdate) {
      core.selectionRange.value = fullyValidated;
    }
  };

  const hasBracketChanges = (
    start: number,
    end: number,
    newNodes: EditorNode[] = []
  ): boolean => {
    // Check if any removed nodes were brackets
    for (let i = start; i < end; i++) {
      const node = editorContent.value[i];
      if (node?.type === EditorNodeName.parenthesis) {
        return true;
      }
    }

    // Check if any new nodes are brackets
    for (const node of newNodes) {
      if (node.type === EditorNodeName.parenthesis) {
        return true;
      }
    }

    return false;
  };

  const updateContent = async (
    start: number,
    end: number,
    newNodes: EditorNode[] = []
  ): Promise<void> => {
    const newContent = createNewContent(start, end, newNodes);

    if (hasBracketChanges(start, end, newNodes)) {
      updateBracketColors(newContent);
    }

    editorContent.value = newContent;
    updateSelectionIfNeeded(newContent);
    emit("update:model-value", newContent);
    await nextTick();
  };

  const shouldDeleteAsRange = (): boolean => {
    if (!core.selectionRange.value) {
      return false;
    }

    const { start, end, startOffset, endOffset } = core.selectionRange.value;
    const currentNode = editorContent.value[start];

    return end !== start || (endOffset !== startOffset && !currentNode?.atomic);
  };

  const deleteSingleCharacter = async (nodeIndex: number): Promise<void> => {
    const currentNode = editorContent.value[nodeIndex];
    if (!currentNode) {
      return;
    }

    const previousNode =
      nodeIndex > 0 ? editorContent.value[nodeIndex - 1] : undefined;
    const currentOffset = core.selectionRange.value?.startOffset || 0;
    let newCursorPosition = currentOffset;
    let newNodeIndex = nodeIndex;

    if (currentNode.atomic || (currentNode.content?.length ?? 0) === 1) {
      await updateContent(nodeIndex, nodeIndex + 1);
      newNodeIndex = Math.max(0, nodeIndex - 1);
      newCursorPosition = previousNode?.content?.length ?? 0;
    } else {
      // Handle backspace deletion - delete character before cursor
      if (currentOffset === 0) {
        // Cursor is at beginning of node - move to previous node if exists
        if (previousNode && !previousNode.atomic) {
          // Merge with previous node by moving cursor to end of previous node
          newNodeIndex = nodeIndex - 1;
          newCursorPosition = previousNode.content?.length ?? 0;
          return await core.updateCursorPosition(
            newNodeIndex,
            newCursorPosition
          );
        }
        return; // Nothing to delete at beginning of first node
      }

      // Delete the character before the cursor position
      const nodeContent = currentNode.content ?? "";
      const newText =
        nodeContent.slice(0, currentOffset - 1) +
        nodeContent.slice(currentOffset);

      const newNodes = tokenize(newText, previousNode);
      await updateContent(nodeIndex, nodeIndex + 1, newNodes);

      // Update cursor position to be one position back
      newCursorPosition = currentOffset - 1;

      // If we merged with previous node content and cursor would be at position 0
      if (
        newCursorPosition === 0 &&
        newNodes.length > 0 &&
        previousNode &&
        !previousNode.atomic
      ) {
        // Check if the new content was merged with previous node
        const mergedLength = previousNode.content?.length ?? 0;
        const prevContent = previousNode.content ?? "";
        if (newNodes[0] && newNodes[0].content?.startsWith(prevContent)) {
          newNodeIndex = nodeIndex - 1;
          newCursorPosition = mergedLength;
        }
      }
    }

    await core.updateCursorPosition(newNodeIndex, newCursorPosition);
  };

  const deleteSelectedRange = async (): Promise<void> => {
    if (!core.selectionRange.value) {
      return;
    }

    const { start, end, startOffset, endOffset } = core.selectionRange.value;

    if (end - start >= 1) {
      const contentCopy = cloneDeep(editorContent.value);
      const previousNode = contentCopy[start - 1];
      const firstNode = contentCopy[start];
      const lastNode = contentCopy[end];

      firstNode.content = firstNode.atomic
        ? ""
        : (firstNode.content ?? "").slice(0, startOffset);
      lastNode.content = lastNode.atomic
        ? ""
        : (lastNode.content ?? "").slice(endOffset);

      const combinedText = (firstNode.content ?? "") + (lastNode.content ?? "");
      const newNodes = combinedText.length
        ? tokenize(combinedText, previousNode)
        : [];

      await updateContent(start, end + 1, newNodes);
    } else {
      const currentNode = editorContent.value[start];
      const previousNode =
        start > 0 ? editorContent.value[start - 1] : undefined;

      const nodeContent = currentNode?.content ?? "";
      const newText =
        nodeContent.slice(0, startOffset) + nodeContent.slice(endOffset);

      const newNodes = tokenize(newText, previousNode);
      await updateContent(start, start + 1, newNodes);
    }

    const startIndex = startOffset === 0 && start > 0 ? start - 1 : start;
    let offset: number;

    if (start === 0 && startOffset === 0) {
      offset = 0;
    } else if (startOffset === 0) {
      offset = editorContent.value[startIndex]?.content?.length ?? 0;
    } else {
      offset = startOffset;
    }

    await core.updateCursorPosition(startIndex, offset);
  };

  return {
    updateContent,
    shouldDeleteAsRange,
    deleteSingleCharacter,
    deleteSelectedRange,
  };
};

// =============================================================================

// =============================================================================

export const useEditorContentV2 = (
  editorContent: Ref<EditorNode[]>,
  core: ReturnType<typeof useEditorCore>,
  parserIntegration: ReturnType<typeof useEditorParserIntegration>,
  emit: (event: "update:model-value", value: EditorNode[]) => void
) => {
  const updateContent = async (
    start: number,
    end: number,
    newNodes: EditorNode[] = []
  ): Promise<void> => {
    editorContent.value = [
      ...editorContent.value.slice(0, start),
      ...newNodes,
      ...editorContent.value.slice(end),
    ];
    emit("update:model-value", editorContent.value);
  };

  const updateContentFromFormula = async (
    newFormula: string,
    newCursorOffset: number
  ): Promise<void> => {
    const newNodes =
      await parserIntegration.updateContentWithParser(newFormula);

    editorContent.value = newNodes;
    emit("update:model-value", newNodes);

    await nextTick();

    const cursorPos = parserIntegration.getCursorPosition(newCursorOffset);
    await core.updateCursorPosition(cursorPos.index, cursorPos.offset);
  };

  const shouldDeleteAsRange = (): boolean => {
    if (!core.selectionRange.value) {
      return false;
    }

    const { start, end, startOffset, endOffset } = core.selectionRange.value;
    const currentNode = editorContent.value[start];

    return end !== start || (endOffset !== startOffset && !currentNode?.atomic);
  };

  const deleteSingleCharacter = async (nodeIndex: number): Promise<void> => {
    const currentNode = editorContent.value[nodeIndex];
    if (!currentNode) {
      return;
    }

    const previousNode =
      nodeIndex > 0 ? editorContent.value[nodeIndex - 1] : undefined;
    const currentOffset = core.selectionRange.value?.startOffset || 0;

    if (currentNode.atomic) {
      const currentFormula = parserIntegration.serializeToFormula();
      const nodeStartOffset = nodePositionToFormulaOffset(
        editorContent.value,
        nodeIndex,
        0
      );
      const nodeLength = getSerializedNodeLength(currentNode);
      const nodeEndOffset = nodeStartOffset + nodeLength;

      const newFormula =
        currentFormula.slice(0, nodeStartOffset) +
        currentFormula.slice(nodeEndOffset);

      await updateContentFromFormula(newFormula, nodeStartOffset);
      return;
    }

    if (currentOffset === 0 && nodeIndex > 0) {
      if (previousNode && !previousNode.atomic) {
        await core.updateCursorPosition(
          nodeIndex - 1,
          previousNode.content?.length ?? 0
        );
        return;
      }
      return;
    }

    const currentFormula = parserIntegration.serializeToFormula();
    const formulaOffset = nodePositionToFormulaOffset(
      editorContent.value,
      nodeIndex,
      currentOffset
    );

    if (formulaOffset === 0) {
      return;
    }

    const newFormula =
      currentFormula.slice(0, formulaOffset - 1) +
      currentFormula.slice(formulaOffset);

    await updateContentFromFormula(newFormula, formulaOffset - 1);
  };

  const deleteSelectedRange = async (): Promise<void> => {
    if (!core.selectionRange.value) {
      return;
    }

    const { start, startOffset, end, endOffset } = core.selectionRange.value;

    const { newNodes, newCursorOffset } =
      await parserIntegration.deleteTextRange(
        start,
        startOffset,
        end,
        endOffset
      );

    editorContent.value = newNodes;
    emit("update:model-value", newNodes);

    await nextTick();

    const cursorPos = parserIntegration.getCursorPosition(newCursorOffset);
    await core.updateCursorPosition(cursorPos.index, cursorPos.offset);
  };

  return {
    updateContent,
    updateContentFromFormula,
    shouldDeleteAsRange,
    deleteSingleCharacter,
    deleteSelectedRange,
  };
};

// =============================================================================
// PARSER-BASED INPUT HANDLING (NEW)
// Simplified input handling using full-formula serialization
// =============================================================================

export const useEditorInputV2 = (
  editorContent: Ref<EditorNode[]>,
  core: ReturnType<typeof useEditorCore>,
  parserIntegration: ReturnType<typeof useEditorParserIntegration>,
  isProcessingKeyInput: Ref<boolean>,
  createEditorNode: (content: string, type: EditorNodeName) => EditorNode,
  emit: (event: "update:model-value", value: EditorNode[]) => void,
  contentComposable: {
    shouldDeleteAsRange: () => boolean;
    deleteSingleCharacter: (nodeIndex: number) => Promise<void>;
    deleteSelectedRange: () => Promise<void>;
    updateContentFromFormula: (
      formula: string,
      cursorOffset: number
    ) => Promise<void>;
  }
) => {
  const shouldInsertUncategorizedNode = (
    currentNode: EditorNode,
    previousNode?: EditorNode,
    currentNodeIndex?: number
  ): boolean => {
    return currentNode.atomic && !previousNode && currentNodeIndex === 0;
  };

  const handleAtomicNodeNavigation = async (
    key: AllowedKey.arrowLeft | AllowedKey.arrowRight,
    currentNodeIndex: number,
    currentNode: EditorNode,
    previousNode?: EditorNode,
    nextNode?: EditorNode
  ): Promise<{ newNodeIndex: number; newPosition: number } | null> => {
    if (key === AllowedKey.arrowLeft) {
      if (
        shouldInsertUncategorizedNode(
          currentNode,
          previousNode,
          currentNodeIndex
        )
      ) {
        editorContent.value.splice(
          currentNodeIndex,
          0,
          createEditorNode("", EditorNodeName.uncategorized)
        );
        await core.updateCursorPosition(currentNodeIndex, 0);
        return null;
      }

      if (currentNodeIndex > 0) {
        const newNodeIndex = currentNodeIndex - 1;
        const newPosition =
          editorContent.value[newNodeIndex]?.content?.length ?? 0;
        return { newNodeIndex, newPosition };
      }
    } else {
      if (currentNodeIndex < editorContent.value.length - 1) {
        const newPosition = nextNode?.atomic
          ? (nextNode.content?.length ?? 0)
          : 0;
        return { newNodeIndex: currentNodeIndex + 1, newPosition };
      }
    }

    return null;
  };

  const handleNonAtomicNodeNavigation = (
    key: AllowedKey.arrowLeft | AllowedKey.arrowRight,
    currentNodeIndex: number,
    currentOffset: number,
    currentNodeElement: HTMLElement | null,
    nextNode?: EditorNode
  ): { newNodeIndex: number; newPosition: number } => {
    if (key === AllowedKey.arrowLeft) {
      const newPosition = Math.max(0, currentOffset - 1);

      if (newPosition === 0 && currentNodeIndex > 0) {
        const previousNodeElement = core.getNodeElement(currentNodeIndex - 1);
        if (previousNodeElement?.textContent) {
          return {
            newNodeIndex: currentNodeIndex - 1,
            newPosition: previousNodeElement.textContent.length,
          };
        }
      }

      return { newNodeIndex: currentNodeIndex, newPosition };
    } else {
      const maxPosition = currentNodeElement?.textContent?.length ?? 0;
      const newPosition = Math.min(maxPosition, currentOffset + 1);

      if (
        newPosition === maxPosition &&
        currentNodeIndex < editorContent.value.length - 1
      ) {
        const nextPosition = nextNode?.atomic
          ? (nextNode.content?.length ?? 0)
          : 0;
        return {
          newNodeIndex: currentNodeIndex + 1,
          newPosition: nextPosition,
        };
      }

      return { newNodeIndex: currentNodeIndex, newPosition };
    }
  };

  const processArrowKeyNavigation = async (
    key: AllowedKey.arrowLeft | AllowedKey.arrowRight
  ): Promise<void> => {
    const currentNodeIndex = core.currentNodeIndex.value;
    const currentNodeElement = core.getNodeElement(currentNodeIndex);
    const currentNode = core.currentNode.value;

    if (!currentNodeElement || !currentNode) {
      return;
    }

    const hasContent =
      (currentNodeElement.lastChild && currentNodeElement.textContent) ||
      currentNode.type === EditorNodeName.uncategorized;

    if (!hasContent) {
      return;
    }

    const previousNode = core.previousNode.value;
    const nextNode = core.nextNode.value;
    const currentOffset = core.selectionRange.value?.startOffset || 0;

    let navigationResult: { newNodeIndex: number; newPosition: number } | null =
      null;

    if (currentNode.atomic) {
      navigationResult = await handleAtomicNodeNavigation(
        key,
        currentNodeIndex,
        currentNode,
        previousNode,
        nextNode
      );
    } else {
      navigationResult = handleNonAtomicNodeNavigation(
        key,
        currentNodeIndex,
        currentOffset,
        currentNodeElement,
        nextNode
      );
    }

    if (navigationResult) {
      await core.updateCursorPosition(
        navigationResult.newNodeIndex,
        navigationResult.newPosition
      );
    }
  };

  const shouldHandleAsRangeSelection = (): boolean => {
    if (!core.selectionRange.value) {
      return false;
    }

    const { start, end, startOffset, endOffset } = core.selectionRange.value;
    return endOffset !== startOffset || end !== start;
  };

  const shouldRedirectToNextNode = (
    currentNode: EditorNode,
    nextNode?: EditorNode
  ): boolean => {
    return currentNode.atomic && !!nextNode && !nextNode.atomic;
  };

  const shouldIgnoreSpaceInput = (): boolean => {
    return false;
  };

  const handleRangeSelection = async (key: string): Promise<void> => {
    if (!core.selectionRange.value) {
      return;
    }

    const startOfSelection = core.selectionRange.value.start;

    if (contentComposable.shouldDeleteAsRange()) {
      await contentComposable.deleteSelectedRange();
    } else {
      await contentComposable.deleteSingleCharacter(
        core.currentNodeIndex.value
      );
    }

    await nextTick();

    const isFirstNodeAtomic = !!editorContent.value[0]?.atomic;
    if (isFirstNodeAtomic && startOfSelection === 0) {
      editorContent.value.unshift(
        createEditorNode("", EditorNodeName.uncategorized)
      );
      await processArrowKeyNavigation(AllowedKey.arrowLeft);
    }

    await processCharacterInput(key);
  };

  const processCharacterInput = async (key: string): Promise<void> => {
    if (isProcessingKeyInput.value) {
      return;
    }
    isProcessingKeyInput.value = true;

    try {
      const currentNodeIndex = core.currentNodeIndex.value;
      const currentNode = core.currentNode.value;
      const nextNode = core.nextNode.value;

      if (shouldHandleAsRangeSelection()) {
        await handleRangeSelection(key);
        return;
      }

      if (!currentNode) {
        return;
      }

      if (shouldRedirectToNextNode(currentNode, nextNode)) {
        await processArrowKeyNavigation(AllowedKey.arrowRight);
        isProcessingKeyInput.value = false;
        await processCharacterInput(key);
        return;
      }

      const currentNodeElement = core.getNodeElement(currentNodeIndex);
      if (!currentNodeElement) {
        return;
      }

      const currentOffset = core.selectionRange.value?.startOffset || 0;

      const { newNodes, newNodeIndex, newNodeOffset } =
        await parserIntegration.insertTextAtCursor(
          key,
          currentNodeIndex,
          currentOffset
        );

      editorContent.value = newNodes;
      emit("update:model-value", newNodes);

      await nextTick();

      const windowSelection = window.getSelection();
      if (!windowSelection || windowSelection.toString().length !== 0) {
        return;
      }

      await core.updateCursorPosition(newNodeIndex, newNodeOffset);
    } finally {
      isProcessingKeyInput.value = false;
    }
  };

  return {
    processArrowKeyNavigation,
    processCharacterInput,
    shouldHandleAsRangeSelection,
    shouldRedirectToNextNode,
    shouldIgnoreSpaceInput,
  };
};

// =============================================================================
// PARSER-BASED BRACKET MANAGEMENT (NEW)
// Simplified bracket handling using parser metadata
// =============================================================================

export const useEditorBracketsV2 = (
  editorContent: Ref<EditorNode[]>,
  currentNodeIndex: Ref<number>,
  parserMetadata: Ref<ParsedFormulaMetadata>,
  setLocalFunctionDefinition: (functionName: string) => void
) => {
  const activeBracketPairRange = ref<ActiveBracketPairRange>({});

  const resetActiveBrackets = (): void => {
    const { startIndex, endIndex } = activeBracketPairRange.value;

    if (
      startIndex !== undefined &&
      endIndex !== undefined &&
      startIndex < endIndex
    ) {
      const startBracket = editorContent.value[startIndex];
      const endBracket = editorContent.value[endIndex];

      if (startBracket?.context) {
        (startBracket.context as BracketContext).isActive = false;
      }
      if (endBracket?.context) {
        (endBracket.context as BracketContext).isActive = false;
      }
    }

    activeBracketPairRange.value = {};
  };

  const updateActiveBracketPairRange = (): void => {
    resetActiveBrackets();

    const activeBracket = findActiveBracketPair(
      currentNodeIndex.value,
      parserMetadata.value
    );

    if (!activeBracket) {
      return;
    }

    const { openingIndex, closingIndex } = activeBracket;

    const openingBracket = editorContent.value[openingIndex];
    const closingBracket = editorContent.value[closingIndex];

    if (openingBracket?.context) {
      (openingBracket.context as BracketContext).isActive = true;
    }

    if (closingBracket?.context) {
      (closingBracket.context as BracketContext).isActive = true;
    }

    activeBracketPairRange.value = {
      startIndex: openingIndex,
      endIndex: closingIndex,
    };

    const nodeBeforeBracket = editorContent.value[openingIndex - 1];
    if (
      nodeBeforeBracket?.type === EditorNodeName.function &&
      !DELIMITERS.includes(nodeBeforeBracket.content)
    ) {
      setLocalFunctionDefinition(nodeBeforeBracket.content);
    }
  };

  const getFunctionArgumentNodes = (): EditorNode[][] => {
    if (!activeBracketPairRange.value.startIndex) {
      return [];
    }

    const startIndex = activeBracketPairRange.value.startIndex;
    const endIndex =
      activeBracketPairRange.value.endIndex === startIndex
        ? currentNodeIndex.value + 1
        : activeBracketPairRange.value.endIndex;

    const argumentNodes: EditorNode[][] = [[]];
    const slicedNodes = editorContent.value.slice(startIndex + 1, endIndex);

    for (const node of slicedNodes) {
      if (node.type === EditorNodeName.separator) {
        argumentNodes.push([]);
      } else {
        argumentNodes[argumentNodes.length - 1].push(node);
      }
    }

    return argumentNodes;
  };

  return {
    updateActiveBracketPairRange,
    resetActiveBrackets,
    getFunctionArgumentNodes,
    activeBracketPairRange,
  };
};

// =============================================================================
// COMPOSABLE FACTORY - SWITCHES BETWEEN V1 AND V2
// =============================================================================

export interface EditorComposablesV1 {
  version: "v1";
  content: ReturnType<typeof useEditorContent>;
  input: ReturnType<typeof useEditorInput>;
  brackets: ReturnType<typeof useEditorBrackets>;
  positioning: ReturnType<typeof useEditorPositioning>;
}

export interface EditorComposablesV2 {
  version: "v2";
  content: ReturnType<typeof useEditorContentV2>;
  input: ReturnType<typeof useEditorInputV2>;
  brackets: ReturnType<typeof useEditorBracketsV2>;
  parserIntegration: ReturnType<typeof useEditorParserIntegration>;
}

export type EditorComposables = EditorComposablesV1 | EditorComposablesV2;

interface CreateEditorComposablesOptions {
  useParserV2: boolean;
  editorContent: Ref<EditorNode[]>;
  core: ReturnType<typeof useEditorCore>;
  formulaEditor: ReturnType<typeof useFormulaEditor>;
  functionsByNameMap: Ref<Record<string, FormulaOption>>;
  serviceAttributes: Ref<IAttribute[]>;
  isProcessingKeyInput: Ref<boolean>;
  createEditorNode: (content: string, type: EditorNodeName) => EditorNode;
  currentNodeIndex: Ref<number>;
  setLocalFunctionDefinition: (functionName: string) => void;
  validateEditorState: () => boolean;
  emit: (event: "update:model-value", value: EditorNode[]) => void;
}

export const createEditorComposables = (
  options: CreateEditorComposablesOptions
): EditorComposables => {
  const {
    useParserV2,
    editorContent,
    core,
    formulaEditor,
    functionsByNameMap,
    serviceAttributes,
    isProcessingKeyInput,
    createEditorNode,
    currentNodeIndex,
    setLocalFunctionDefinition,
    validateEditorState,
    emit,
  } = options;

  if (useParserV2) {
    const parserIntegration = useEditorParserIntegration(
      editorContent,
      functionsByNameMap,
      serviceAttributes
    );

    const content = useEditorContentV2(
      editorContent,
      core,
      parserIntegration,
      emit
    );

    const input = useEditorInputV2(
      editorContent,
      core,
      parserIntegration,
      isProcessingKeyInput,
      createEditorNode,
      emit,
      {
        shouldDeleteAsRange: content.shouldDeleteAsRange,
        deleteSingleCharacter: content.deleteSingleCharacter,
        deleteSelectedRange: content.deleteSelectedRange,
        updateContentFromFormula: content.updateContentFromFormula,
      }
    );

    const brackets = useEditorBracketsV2(
      editorContent,
      currentNodeIndex,
      parserIntegration.metadata,
      setLocalFunctionDefinition
    );

    return {
      version: "v2",
      content,
      input,
      brackets,
      parserIntegration,
    };
  }

  const positioning = useEditorPositioning(editorContent, core);

  const brackets = useEditorBrackets(
    editorContent,
    currentNodeIndex,
    setLocalFunctionDefinition
  );

  const content = useEditorContent(
    editorContent,
    core,
    formulaEditor,
    createEditorNode,
    brackets.updateBracketColors,
    emit
  );

  const input = useEditorInput(
    editorContent,
    core,
    formulaEditor,
    isProcessingKeyInput,
    createEditorNode,
    content.updateContent,
    positioning.calculateNewPosition,
    validateEditorState,
    {
      shouldDeleteAsRange: content.shouldDeleteAsRange,
      deleteSingleCharacter: content.deleteSingleCharacter,
      deleteSelectedRange: content.deleteSelectedRange,
    }
  );

  return {
    version: "v1",
    content,
    input,
    brackets,
    positioning,
  };
};

// =============================================================================
// 4. BRACKET MANAGEMENT AND FUNCTION HANDLING
// Handle bracket matching, coloring, and function options
// =============================================================================

export const useEditorBrackets = (
  editorContent: Ref<EditorNode[]>,
  currentNodeIndex: Ref<number>,
  setLocalFunctionDefinition: (functionName: string) => void
) => {
  const activeBracketPairRange = ref<ActiveBracketPairRange>({});

  const shouldProcessBracketNode = (node: EditorNode): boolean => {
    return node.type === EditorNodeName.parenthesis;
  };

  const isOpeningBracket = (node: EditorNode): boolean => {
    return node.content === BracketOperator.open;
  };

  const isClosingBracket = (node: EditorNode): boolean => {
    return node.content === BracketOperator.close;
  };

  const processOpeningBracket = (
    node: EditorNode,
    index: number,
    bracketIndex: number,
    bracketGroupID: string | undefined,
    bracketStack: EditorNode[]
  ): { newBracketGroupID: string | undefined } => {
    const color = BRACKET_COLORS[bracketIndex % BRACKET_COLORS.length];
    const newBracketGroupID = bracketGroupID ?? node.id;

    node.context = {
      matchedBracketsId: undefined,
      color,
      matchedBracketsIndex: index,
    };

    bracketStack.push(node);
    return { newBracketGroupID };
  };

  const processClosingBracket = (
    node: EditorNode,
    index: number,
    bracketStack: EditorNode[],
    bracketGroupID: string | undefined
  ): { newBracketGroupID: string | undefined; newBracketIndex: number } => {
    const openingBracket = bracketStack.pop();

    if (!openingBracket) {
      node.context = {
        matchedBracketsId: undefined,
        color: "",
      };
      return { newBracketGroupID: bracketGroupID, newBracketIndex: -1 };
    }

    const openingContext = openingBracket.context as BracketContext;
    const { color = "", matchedBracketsIndex = -1 } = openingContext;

    openingBracket.context = {
      color,
      matchedBracketsId: node.id,
      matchedBracketsIndex: index,
    };

    node.context = {
      color,
      matchedBracketsId: openingBracket.id,
      matchedBracketsIndex,
    };

    const isGroupEnd = openingBracket.id === bracketGroupID;
    return {
      newBracketGroupID: isGroupEnd ? undefined : bracketGroupID,
      newBracketIndex: -1,
    };
  };

  let lastBracketSignature = "";

  const updateBracketColors = (nodes: EditorNode[]): void => {
    if (!nodes.length) {
      return;
    }

    const signatureParts: string[] = [];
    for (const node of nodes) {
      if (shouldProcessBracketNode(node)) {
        signatureParts.push(`${node.id}:${node.content}`);
      }
    }

    const bracketSignature = signatureParts.join("|");
    if (!bracketSignature || bracketSignature === lastBracketSignature) {
      return;
    }

    const bracketStack: EditorNode[] = [];
    let bracketIndex = 0;
    let bracketGroupID: string | undefined = undefined;

    for (let index = 0; index < nodes.length; index++) {
      const node = nodes[index];

      if (shouldProcessBracketNode(node)) {
        if (isOpeningBracket(node)) {
          const result = processOpeningBracket(
            node,
            index,
            bracketIndex,
            bracketGroupID,
            bracketStack
          );
          bracketGroupID = result.newBracketGroupID;
        } else if (isClosingBracket(node)) {
          const result = processClosingBracket(
            node,
            index,
            bracketStack,
            bracketGroupID
          );
          bracketGroupID = result.newBracketGroupID;
          if (result.newBracketIndex !== -1) {
            bracketIndex = result.newBracketIndex;
          }
        }
        bracketIndex++;
      }
    }

    lastBracketSignature = bracketSignature;
  };

  const resetActiveBrackets = (): void => {
    const { startIndex, endIndex } = activeBracketPairRange.value;

    if (
      startIndex !== undefined &&
      endIndex !== undefined &&
      startIndex < endIndex
    ) {
      const startBracket = editorContent.value[startIndex];
      const endBracket = editorContent.value[endIndex];

      if (startBracket?.context) {
        (startBracket.context as BracketContext).isActive = false;
      }
      if (endBracket?.context) {
        (endBracket.context as BracketContext).isActive = false;
      }
    }

    activeBracketPairRange.value = {};
  };

  const findNearestOpeningBracket = (): {
    node: EditorNode;
    index: number;
  } | null => {
    const currentIndex = currentNodeIndex.value;
    const nodes = editorContent.value;

    for (let i = currentIndex; i >= 0; i--) {
      const node = nodes[i];

      if (
        !node ||
        node.type !== EditorNodeName.parenthesis ||
        node.content !== BracketOperator.open
      ) {
        continue;
      }

      const context = node.context as BracketContext;
      const matchedIndex = context?.matchedBracketsIndex ?? -1;
      const hasNoMatchedId = !context?.matchedBracketsId;
      const isInValidRange = matchedIndex >= currentIndex || hasNoMatchedId;

      if (isInValidRange) {
        return { node, index: i };
      }
    }

    return null;
  };

  let cachedBracketResult: {
    result: { node: EditorNode; index: number } | null;
    currentIndex: number;
    contentSignature: string;
  } | null = null;

  const getBracketContentSignature = (): string => {
    const signatureParts: string[] = [];
    for (const node of editorContent.value) {
      if (node.type === EditorNodeName.parenthesis) {
        signatureParts.push(`${node.id}:${node.content}`);
      }
    }
    return signatureParts.join("|");
  };

  const findNearestOpeningBracketCached = (): {
    node: EditorNode;
    index: number;
  } | null => {
    const currentIndex = currentNodeIndex.value;
    const contentSignature = getBracketContentSignature();

    if (
      cachedBracketResult &&
      cachedBracketResult.currentIndex === currentIndex &&
      cachedBracketResult.contentSignature === contentSignature
    ) {
      return cachedBracketResult.result;
    }

    const result = findNearestOpeningBracket();

    cachedBracketResult = {
      result,
      currentIndex,
      contentSignature,
    };

    return result;
  };

  const updateActiveBracketPairRange = (): void => {
    resetActiveBrackets();

    const nearestBracket = findNearestOpeningBracketCached();
    if (!nearestBracket) {
      return;
    }

    const { node: nearestOpeningBracket, index: indexOfNearestOpeningBracket } =
      nearestBracket;
    const bracketContext = nearestOpeningBracket.context as BracketContext;
    const endIndex = bracketContext.matchedBracketsIndex ?? -1;

    const shouldActivate =
      !!endIndex &&
      endIndex !== -1 &&
      endIndex !== indexOfNearestOpeningBracket &&
      indexOfNearestOpeningBracket <= currentNodeIndex.value &&
      (currentNodeIndex.value <= endIndex || endIndex === -1);

    if (shouldActivate) {
      bracketContext.isActive = true;

      if (endIndex !== -1) {
        const closingBracket = editorContent.value[endIndex];
        if (closingBracket?.context) {
          (closingBracket.context as BracketContext).isActive = true;
        }
      }
    }

    activeBracketPairRange.value = {
      startIndex: indexOfNearestOpeningBracket,
      endIndex,
    };

    // Check for function bracket
    const nodeBeforeBracket =
      editorContent.value[indexOfNearestOpeningBracket - 1];
    if (
      nodeBeforeBracket?.type === EditorNodeName.function &&
      !DELIMITERS.includes(nodeBeforeBracket.content)
    ) {
      setLocalFunctionDefinition(nodeBeforeBracket.content);
    }
  };

  const getFunctionArgumentNodes = (): EditorNode[][] => {
    if (!activeBracketPairRange.value.startIndex) {
      return [];
    }

    const startIndex = activeBracketPairRange.value.startIndex;
    const endIndex =
      activeBracketPairRange.value.endIndex === startIndex
        ? currentNodeIndex.value + 1
        : activeBracketPairRange.value.endIndex;

    // Performance optimization: Single pass through slice instead of reduce
    const argumentNodes: EditorNode[][] = [[]];
    const slicedNodes = editorContent.value.slice(startIndex + 1, endIndex);

    for (const node of slicedNodes) {
      if (node.type === EditorNodeName.separator) {
        argumentNodes.push([]);
      } else {
        argumentNodes[argumentNodes.length - 1].push(node);
      }
    }

    return argumentNodes;
  };

  return {
    updateBracketColors,
    updateActiveBracketPairRange,
    resetActiveBrackets,
    getFunctionArgumentNodes,
    activeBracketPairRange,
  };
};

// =============================================================================
// 5. CLIPBOARD AND PASTE OPERATIONS
// Handle copy/paste operations and clipboard management
// =============================================================================

export const useEditorClipboard = (
  editorContent: Ref<EditorNode[]>,
  selectionRange: Ref<EditorRange | null>,
  handleCharacterKey: (key: string) => Promise<void>,
  recordEditorState: () => Promise<void>
) => {
  // Clipboard utilities
  const attributeToString = (attributeNode: EditorNode): string => {
    const context = attributeNode.context;
    const contextString =
      context && Object.keys(context).length > 0 ? JSON.stringify(context) : "";
    return `:${attributeNode.attributeId}${contextString}`;
  };

  const getSelectedNodes = (): EditorNode[] => {
    if (!selectionRange.value) {
      return [];
    }

    return editorContent.value.slice(
      selectionRange.value.start,
      selectionRange.value.end + 1
    );
  };

  const processNodeForClipboard = (
    node: EditorNode,
    isFirst: boolean,
    isLast: boolean,
    isSingle: boolean
  ): string => {
    if (node.attributeId) {
      return attributeToString(node);
    }

    if (isSingle && selectionRange.value) {
      return node.content.substring(
        selectionRange.value.startOffset,
        selectionRange.value.endOffset
      );
    }

    if (isFirst && selectionRange.value?.startOffset) {
      return node.content.substring(selectionRange.value.startOffset);
    }

    if (isLast && selectionRange.value?.endOffset) {
      return node.content.substring(0, selectionRange.value.endOffset);
    }

    return node.content;
  };

  const generateClipboardContent = (): string => {
    const selectedNodes = getSelectedNodes();
    if (selectedNodes.length === 0) {
      return "";
    }

    if (selectedNodes.length === 1) {
      return processNodeForClipboard(selectedNodes[0], false, false, true);
    }

    return selectedNodes
      .map((node, index) => {
        const isFirst = index === 0;
        const isLast = index === selectedNodes.length - 1;
        return processNodeForClipboard(node, isFirst, isLast, false);
      })
      .join("");
  };

  const copyToClipboard = async (content: string): Promise<void> => {
    // Try modern Clipboard API first
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(content);
        return;
      } catch {
        // Fall through to legacy method
      }
    }

    // Fallback: use execCommand (deprecated but widely supported)
    const textArea = document.createElement("textarea");
    textArea.value = content;
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";
    textArea.style.top = "-9999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand("copy");
    } finally {
      document.body.removeChild(textArea);
    }
  };

  const copyContent = async (): Promise<void> => {
    try {
      let clipboardContent = "";

      if (selectionRange.value) {
        clipboardContent = generateClipboardContent();
      }

      // Fallback: if our tracking didn't capture selection, use browser's selection
      if (!clipboardContent) {
        const selection = window.getSelection();
        if (selection && selection.toString()) {
          clipboardContent = selection.toString();
        }
      }

      if (clipboardContent) {
        await copyToClipboard(clipboardContent);
      }
    } catch (error) {
      console.warn("Failed to copy content to clipboard:", error);
    }
  };

  // Paste handling
  const handlePaste = async (event: ClipboardEvent): Promise<void> => {
    const clipboardData = event.clipboardData;
    if (!clipboardData) {
      return;
    }

    const text = clipboardData.getData("text/plain");
    if (!text) {
      return;
    }

    await handleCharacterKey(text);
    await recordEditorState();
  };

  return {
    copyContent,
    handlePaste,
    generateClipboardContent,
  };
};

// =============================================================================
// 6. FUNCTION OPTIONS AND FORMULA HANDLING
// Handle function parameter options and formula validation
// =============================================================================

export const useEditorFunctionOptions = (
  localFunctionDefinition: Ref<LocalFunctionDefinition | null>,
  attributeOptions: Ref<Record<string, unknown>>,
  dispatch: (action: string, payload: unknown) => void
) => {
  const hasValidFunctionDefinition = (): boolean => {
    return !!(
      localFunctionDefinition.value?.arguments.length ||
      localFunctionDefinition.value?.definition?.parameters?.length
    );
  };

  const getCurrentArgumentDefinition = (
    index: number
  ): FormulaParameter | undefined => {
    if (!localFunctionDefinition.value?.definition?.parameters) {
      return undefined;
    }

    const parameters = localFunctionDefinition.value.definition.parameters;
    const parametersLength = parameters.length;

    if (index <= parametersLength - 1) {
      return parameters[index];
    }

    const isLastParameterVariadic = parameters[parametersLength - 1]?.variadic;
    if (index > parametersLength - 1 && isLastParameterVariadic) {
      return parameters[parametersLength - 1];
    }

    return undefined;
  };

  const isArgumentAttributeWithOptions = (
    argument: EditorNode[],
    currentArgumentDefinition?: FormulaParameter
  ): boolean => {
    if (
      !currentArgumentDefinition?.name ||
      !localFunctionDefinition.value?.definition?.parameters
    ) {
      return false;
    }

    const isAttributeArgument =
      argument.length === 1 && argument[0].type === EditorNodeName.attribute;

    if (!isAttributeArgument) {
      return false;
    }

    return localFunctionDefinition.value.definition.parameters.some(
      (parameter) =>
        currentArgumentDefinition.name &&
        parameter.relates_to === currentArgumentDefinition.name
    );
  };

  const processArgument = async (
    argument: EditorNode[],
    index: number
  ): Promise<void> => {
    if (!argument.length) {
      return;
    }

    const currentArgumentDefinition = getCurrentArgumentDefinition(index);

    if (isArgumentAttributeWithOptions(argument, currentArgumentDefinition)) {
      const attributeId = argument[0].attributeId;

      if (
        currentArgumentDefinition?.name &&
        attributeId &&
        localFunctionDefinition.value
      ) {
        const optionsAlreadySet =
          currentArgumentDefinition.name in
          localFunctionDefinition.value.optionsSet;
        const optionsAlreadyCached = attributeId in attributeOptions.value;

        if (!optionsAlreadySet && !optionsAlreadyCached) {
          dispatch("options/getSingleAttributeOptions", attributeId);
        }

        localFunctionDefinition.value.optionsSet[
          currentArgumentDefinition.name
        ] = attributeId;
      }
    }

    // Set related options
    if (
      currentArgumentDefinition?.relates_to &&
      localFunctionDefinition.value
    ) {
      const relatedOptionsKey =
        localFunctionDefinition.value.optionsSet[
          currentArgumentDefinition.relates_to
        ];

      if (relatedOptionsKey) {
        argument[0].optionsKey = relatedOptionsKey;
      }
    }
  };

  const setFunctionNodeOptions = async (): Promise<void> => {
    if (
      !hasValidFunctionDefinition() ||
      !localFunctionDefinition.value?.arguments
    ) {
      return;
    }

    for (const [
      index,
      argument,
    ] of localFunctionDefinition.value.arguments.entries()) {
      await processArgument(argument, index);
    }
  };

  return {
    setFunctionNodeOptions,
    hasValidFunctionDefinition,
    getCurrentArgumentDefinition,
    processArgument,
  };
};

// =============================================================================
// 7. DROPDOWN POSITIONING
// Handle dropdown positioning and layout calculations
// =============================================================================

export const useEditorDropdown = (
  editor: Ref<HTMLElement | null>,
  core: ReturnType<typeof useEditorCore>
) => {
  const getDropdownPosition = (currentNodeIndex: number) => {
    if (!editor.value) {
      return {};
    }

    try {
      const currentNode = core
        .getNodeElement(currentNodeIndex)
        ?.getBoundingClientRect();
      if (!currentNode) {
        return {};
      }

      const dropdownHeight = 288;
      const dropdownWidth = 360;

      const isClippedHorizontally =
        currentNode.left + dropdownWidth > document.body.clientWidth;
      const isClippedVertically =
        currentNode.top + currentNode.height + dropdownHeight >
        document.body.clientHeight;

      return {
        top:
          (isClippedVertically
            ? currentNode.bottom - currentNode.height
            : currentNode.top + currentNode.height) + "px",
        left:
          (isClippedHorizontally ? currentNode.right : currentNode.left) + "px",
        transform: `translate(${isClippedHorizontally ? "-100%" : "0"}, ${
          isClippedVertically ? "-100%" : "0"
        })`,
      };
    } catch (error) {
      console.warn("Failed to calculate dropdown position:", error);
      return {};
    }
  };

  return {
    getDropdownPosition,
  };
};

// =============================================================================
// 8. POSITION CALCULATION
// Calculate cursor positions after content changes
// =============================================================================

export const useEditorPositioning = (
  editorContent: Ref<EditorNode[]>,
  core: ReturnType<typeof useEditorCore>
) => {
  const calculateNewPosition = (newNodes: EditorNode[]): PositionResult => {
    const currentNodeIndex = core.currentNodeIndex.value;
    const startingNode = editorContent.value[currentNodeIndex];

    if (!startingNode || !newNodes.length) {
      return { newIndex: 0, newOffset: 0 };
    }

    if (startingNode.atomic) {
      const insertionOffset = 1;
      const lastNode = newNodes[newNodes.length - 1];
      return {
        newIndex: currentNodeIndex + newNodes.length - insertionOffset,
        newOffset: lastNode?.content?.length ?? 0,
      };
    }

    let initialPosition = currentNodeIndex;
    const includePreviousNode =
      !!editorContent.value[initialPosition - 1] &&
      !editorContent.value[initialPosition - 1]?.atomic;

    const includeNextNode =
      !!editorContent.value[initialPosition + 1] &&
      !editorContent.value[initialPosition + 1]?.atomic;

    const newNodesContentLength = newNodes.reduce(
      (acc, node) => acc + (node.content?.length ?? 0),
      0
    );
    const includedLengthFromPreviousNode = includePreviousNode
      ? (editorContent.value[initialPosition - 1]?.content?.length ?? 0)
      : 0;
    const includedLengthFromNextNode = includeNextNode
      ? (editorContent.value[initialPosition + 1]?.content?.length ?? 0)
      : 0;

    const insertionLengthOffset =
      (editorContent.value[initialPosition]?.content?.length ?? 0) +
      includedLengthFromNextNode +
      includedLengthFromPreviousNode;

    const insertionLength = newNodesContentLength - insertionLengthOffset;

    const startingPosition = core.selectionRange.value?.startOffset ?? 0;
    const startOffset =
      startingPosition +
      (includePreviousNode
        ? (editorContent.value[initialPosition - 1]?.content?.length ?? 0)
        : 0);

    let finalAbsoluteOffset = startOffset + insertionLength;
    for (let i = 0; i < newNodes.length; i++) {
      const nodeContentLength = newNodes[i]?.content?.length ?? 0;
      if (finalAbsoluteOffset <= nodeContentLength) {
        if (includePreviousNode) {
          initialPosition = initialPosition - 1;
        }

        return {
          newIndex: initialPosition + i,
          newOffset: newNodes[i]?.atomic
            ? nodeContentLength
            : finalAbsoluteOffset,
        };
      }
      finalAbsoluteOffset -= nodeContentLength;
    }
    return {
      newIndex: 0,
      newOffset: 0,
    };
  };

  return {
    calculateNewPosition,
  };
};

// =============================================================================
// 9. HISTORY AND UNDO FUNCTIONALITY
// Handle editor state history and undo operations
// =============================================================================

interface EditorStateRecord {
  editorContent: EditorNode[];
  editorSelectionState: EditorRange;
}

export const useEditorHistory = (
  editorContent: Ref<EditorNode[]>,
  core: ReturnType<typeof useEditorCore>,
  createEditorNode: (content: string, type: EditorNodeName) => EditorNode,
  updateBracketColors: (content: EditorNode[]) => void
) => {
  const historyStack = ref<EditorStateRecord[]>([]);
  const MAX_HISTORY_SIZE = 50;

  const recordEditorState = async (): Promise<void> => {
    await nextTick();
    if (!core.selectionRange.value) {
      return;
    }

    // Only record state if it's actually different from the last recorded state
    const lastRecord = historyStack.value[historyStack.value.length - 1];
    if (lastRecord && core.isValidSelection()) {
      const contentSame =
        JSON.stringify(lastRecord.editorContent) ===
        JSON.stringify(editorContent.value);
      const selectionSame =
        JSON.stringify(lastRecord.editorSelectionState) ===
        JSON.stringify(core.selectionRange.value);

      if (contentSame && selectionSame) {
        return;
      }
    }

    const newStateRecord: EditorStateRecord = {
      editorContent: cloneDeep(editorContent.value),
      editorSelectionState: cloneDeep(core.selectionRange.value),
    };
    historyStack.value.push(newStateRecord);

    if (historyStack.value.length > MAX_HISTORY_SIZE) {
      historyStack.value.shift();
    }
  };

  const returnToPreviousEditorState = async (): Promise<void> => {
    if (historyStack.value.length <= 1) {
      return;
    }

    const initialState = historyStack.value.pop();
    const previousState = historyStack.value[historyStack.value.length - 1];
    const previousSelection = previousState?.editorSelectionState;

    if (!previousState || !previousSelection) {
      editorContent.value = initialState?.editorContent ?? [
        createEditorNode("", EditorNodeName.uncategorized),
      ];

      // Wait for DOM to update before setting cursor position
      await nextTick();
      await core.updateCursorPosition(
        initialState?.editorSelectionState.start ?? 0,
        initialState?.editorSelectionState.startOffset ?? 0
      );
      return;
    }

    const newContent = previousState?.editorContent ?? [
      createEditorNode("", EditorNodeName.uncategorized),
    ];

    // Only process brackets if the restored content contains brackets
    const hasBrackets = newContent.some(
      (node) => node.type === EditorNodeName.parenthesis
    );
    if (hasBrackets) {
      updateBracketColors(newContent);
    }

    editorContent.value = newContent;

    // Wait for DOM to update before setting cursor position
    await nextTick();
    await core.updateCursorPosition(
      previousSelection?.start ?? 0,
      previousSelection?.startOffset ?? 0
    );
  };

  return {
    historyStack,
    recordEditorState,
    returnToPreviousEditorState,
  };
};

// =============================================================================
// 10. KEYBOARD SHORTCUTS
// Handle keyboard shortcuts and special key combinations
// =============================================================================

export const useEditorShortcuts = (
  core: ReturnType<typeof useEditorCore>,
  editorContent: Ref<EditorNode[]>,
  clipboard: ReturnType<typeof useEditorClipboard>,
  history: ReturnType<typeof useEditorHistory>
) => {
  const getTextNode = (nodeIndex: number): ChildNode | null => {
    const nodeEl = core.getNodeElement(nodeIndex);
    return nodeEl?.querySelector("[data-node-content]")?.firstChild ?? null;
  };

  const selectAll = (): void => {
    const selection = window.getSelection();
    if (!selection) {
      return;
    }

    try {
      const lastNodeIndex = editorContent.value.length - 1;
      const firstTextNode = getTextNode(0);
      const lastTextNode = getTextNode(lastNodeIndex);

      if (!firstTextNode || !lastTextNode) {
        return;
      }

      const lastNodeContent = editorContent.value[lastNodeIndex]?.content ?? "";
      const range = document.createRange();

      range.setStart(firstTextNode, 0);
      range.setEnd(lastTextNode, lastNodeContent.length);
      selection.removeAllRanges();
      selection.addRange(range);

      core.selectionRange.value = {
        start: 0,
        end: lastNodeIndex,
        startOffset: 0,
        endOffset: lastNodeContent.length,
        selectionDirection: SelectionDirection.ltr,
      };
    } catch (error) {
      console.warn("Failed to select all content:", error);
    }
  };

  const handleShortcuts = (event: KeyboardEvent): void => {
    const ALLOWED_SHORTCUT_KEYS = ["a", "z", "c", "v"];
    const shortcutKey = event.key.toLowerCase();

    if (!ALLOWED_SHORTCUT_KEYS.includes(shortcutKey)) {
      event.preventDefault();
      return;
    }

    const keyHandlers = {
      a: selectAll,
      z: history.returnToPreviousEditorState,
      c: clipboard.copyContent,
    };

    if (shortcutKey in keyHandlers) {
      keyHandlers[shortcutKey as keyof typeof keyHandlers]();
    }
  };

  return {
    selectAll,
    handleShortcuts,
  };
};
